import React, { useEffect, useState } from 'react';
import { createStyles, Grid, makeStyles } from '@material-ui/core';
import SidebarNavigation, { SideBarMenuItemProps } from '../../ui/SidebarNavigation';
import { MessageView } from 'neon-design-system';
import { Redirect, Router, Switch, useHistory, useLocation } from 'react-router-dom';
import AuthenticatedRoute from '../../ui/AuthenticatedRoute';
import { publisherRoutes, Route } from '../../../routes';
import { ArticleFilterProvider } from '../../providers/admin/ArticleFilterProvider';
import { ResearchRole } from '../../../shared/global-enums';
import { useUser } from '../../providers/UserProvider';
import { baseUrls } from '../../../utils/baseUrls.utils';
import { premiumChannelsEnabled } from '../../../utils/utils';

const useStyles = makeStyles(theme => createStyles({
	root: {
		position: 'absolute',
		display: 'flex',
		top: 48,
		bottom: 0,
		left: 0,
		right: 0,
		flexDirection: 'row'
	},
	left: {
		display: 'flex',
		width: 280
	},
	right: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		width: 'calc(100% - 280px)'
	},
	small: {
		flex: 1,
		flexDirection: 'row',
		justifyContent: 'center',
		alignContent: 'center',
		width: '100%',
		[theme.breakpoints.up('lg')]: {
			display: 'none'
		}
	},
	large: {
		[theme.breakpoints.down('md')]: {
			display: 'none'
		}
	},
	content: {
		width: '100%',
		height: '100%'
	}
}));

interface SideBarMenuItemWithUrl extends SideBarMenuItemProps {
	url: string;
	permittedRoles?: ResearchRole[];
}

const PublisherContent = (): JSX.Element => {
	// TODO: SideBarMenuItemProps doesn't include 'url' key, we should not pass 'url' down to SidebarNavigation component, SideBarMenuItemWithUrl interface is a temporary fix
	const [menuItems, setMenuItems] = useState<SideBarMenuItemWithUrl[]>([
		{
			name: 'Articles',
			url: baseUrls.publisher.articles.list
		},
		{
			name: 'Templates',
			url: baseUrls.publisher.templates.list,
			permittedRoles: [ResearchRole.Administrator, ResearchRole.Editor, ResearchRole.Contributor]
		},
		{
			name: 'Channels',
			url: baseUrls.publisher.premiumChannels.list,
			permittedRoles: [ResearchRole.Administrator, ResearchRole.Editor, ResearchRole.Contributor]
		},
		{
			name: premiumChannelsEnabled() ? 'Topics' : 'Categories',
			url: premiumChannelsEnabled() ? baseUrls.publisher.topics.list : baseUrls.publisher.categories.list
		},
		{
			name: 'Tags',
			url: baseUrls.publisher.tags.list
		},
		{
			name: 'Users',
			url: baseUrls.publisher.users.list
		},
		{
			name: 'Desks',
			url: baseUrls.publisher.desks.list
		},
		{
			name: 'Promotional Content',
			url: baseUrls.publisher.promotionalContent.list,
			permittedRoles: [ResearchRole.Administrator]
		}
	].filter(t => t.name === 'Channels' ? premiumChannelsEnabled(false) : true));

	const classes = useStyles();
	const history = useHistory();
	const location = useLocation();
	const { hasScope } = useUser();

	const onSideBarChange = (menuItem: SideBarMenuItemWithUrl): void => {
		history.push(menuItem.url);
	};

	useEffect(() => {
		setMenuItems(items => items.map(menuItem => ({
			name: menuItem.name,
			url: menuItem.url,
			selected: menuItem.url === baseUrls.publisher.promotionalContent.list
				? location.pathname.startsWith(menuItem.url) || location.pathname.startsWith(baseUrls.publisher.promotionalPage.list)
				: location.pathname.startsWith(menuItem.url),
			permittedRoles: menuItem.permittedRoles
		})));
	}, [location.pathname]);

	const permittedMenuItems = menuItems.filter((item) => {
		if (item.permittedRoles) {
			return item.permittedRoles.some(hasScope)
		}

		return true;
	})

	return (
		<div className={classes.root}>
			<Grid container className={classes.large}>
				<Grid item className={classes.left}>
					<SidebarNavigation
						id={'publisher-sidebar'}
						menuItems={permittedMenuItems}
						onChange={(item: SideBarMenuItemProps) => {
							// TODO: SideBarMenuItemProps doesn't include 'url' key, we should not pass 'url' down to SidebarNavigation component, SideBarMenuItemWithUrl interface is a temporary fix
							const menuItem: SideBarMenuItemWithUrl = item as SideBarMenuItemWithUrl;
							onSideBarChange(menuItem);
						}} />
				</Grid>
				<Grid item className={classes.right}>
					<div className={classes.content}>
						<ArticleFilterProvider>
							<Router history={history}>
								<Switch>
									<Redirect from={baseUrls.publisher.main} exact to={baseUrls.publisher.articles.list} />
									{publisherRoutes.map((route: Route<undefined>, i: number) =>
										<AuthenticatedRoute exact key={i} {...route} />)}
								</Switch>
							</Router>
						</ArticleFilterProvider>
					</div>
				</Grid>
			</Grid>
			<Grid container className={classes.small}>
				<MessageView styleName="grey" size="medium" title="Please resize your browser">
					This application cannot work on small devices. For the best experience,
					please use a web browser on a desktop or laptop computer.
				</MessageView>
			</Grid>
		</div>
	);
};

export default PublisherContent;
